<template>
    <div>
        <div class="gradient-box gradient-box-peach" />
        <div class="row" data-test-view="surveys">
            <div class="col-md-2 hidden-xs hidden-sm">
                <l-surveys-menus :rights="rights" :survey="survey"></l-surveys-menus>
            </div>
            <div class="col-md-10">
                <l-card-content>
                    <l-headline
                        :title="survey.name"
                        :sub-title="``"
                        btn-test-id="surveys-insights"
                        :tabs="tabs"
                    >
                    </l-headline>

                    <hr class="visible-xs" />

                    <surveys-insights-list
                        v-if="view == 'list'"
                        ref="currentTab"
                        :survey="survey"
                        @change="changeAnonymity"
                    ></surveys-insights-list>
                    <surveys-insights-heatmap
                        v-if="view == 'heat_map'"
                        ref="currentTab"
                        :survey="survey"
                        @change="changeAnonymity"
                    ></surveys-insights-heatmap>
                    <surveys-insights-participation-rate
                        v-if="view == 'participation_rate'"
                        ref="currentTab"
                        :survey="survey"
                    ></surveys-insights-participation-rate>
                    <surveys-insights-comments
                        v-if="view == 'comments'"
                        ref="currentTab"
                        :survey="survey"
                        @change="changeAnonymity"
                    ></surveys-insights-comments>
                    <surveys-insights-multi
                        v-if="view == 'multi'"
                        ref="currentTab"
                        :survey="survey"
                        @change="changeAnonymity"
                    ></surveys-insights-multi>
                    <surveys-insights-action-items
                        v-if="view == 'action_items'"
                        ref="currentTab"
                        :survey="survey"
                        @change="changeAnonymity"
                    ></surveys-insights-action-items>
                </l-card-content>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";

import icon from "@/img/icons/surveys.png";
import SurveysInsightsHeatmap from "./SurveysInsightsHeatmap.vue";
import SurveysInsightsList from "./SurveysInsightsList.vue";
import SurveysInsightsComments from "./SurveysInsightsComments.vue";
import SurveysInsightsMulti from "./SurveysInsightsMulti.vue";
import SurveysInsightsActionItems from "./SurveysInsightsActionItems.vue";
import SurveysInsightsParticipationRate from "./SurveysInsightsParticipationRate.vue";
import LSurveysMenus from "@/components/LSurveysMenus.vue";

export default {
    components: {
        LCardContent,
        LHeadline,
        SurveysInsightsHeatmap,
        SurveysInsightsList,
        SurveysInsightsComments,
        SurveysInsightsMulti,
        SurveysInsightsActionItems,
        SurveysInsightsParticipationRate,
        LSurveysMenus,
    },
    props: {},
    data() {
        return {
            anonymityThreshold: 3,

            survey: {},
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            user: "users/current/user",
            teamSettings: "users/current/teamSettings",

            getDetail: "surveys/surveys/detail",
        }),
        view() {
            return this.$route.params.tab;
        },
        accountName() {
            // company name
            return this.teamSettings.name;
        },
        tabs() {
            return [
                {
                    label: this.$t("surveys.details.labelList"),
                    path: `/surveys/insights/${this.$route.params.id}/list`,
                    show: this.showTab("LIST"),
                    active: this.$route.params.tab === "list",
                },
                {
                    label: this.$t("surveys.details.labelHeatmap"),
                    path: `/surveys/insights/${this.$route.params.id}/heat_map`,
                    show: this.showTab("HEAT_MAP"),
                    active: this.$route.params.tab === "heat_map",
                },
                {
                    label: this.$t("surveys.details.labelParticipationRate"),
                    path: `/surveys/insights/${this.$route.params.id}/participation_rate`,
                    show: this.showTab("PARTICIPATION_RATE"),
                    active: this.$route.params.tab === "participation_rate",
                },
                {
                    label: this.$t("surveys.details.labelMulti"),
                    path: `/surveys/insights/${this.$route.params.id}/multi`,
                    show: this.showTab("MULTI"),
                    active: this.$route.params.tab === "multi",
                },
                {
                    label: this.$t("surveys.details.labelComments"),
                    path: `/surveys/insights/${this.$route.params.id}/comments`,
                    show: true, // we shall just always show COMMENTS tab. so use view its own comments.
                    active: this.$route.params.tab === "comments",
                },
                {
                    label: this.$t("surveys.details.labelActionItems"),
                    path: `/surveys/insights/${this.$route.params.id}/action_items`,
                    show: true,
                    active: this.$route.params.tab === "action_items",
                },
                {
                    show: !["action_items", "participation_rate"].includes(this.$route.params.tab),
                    label: this.permissionDescription.label,
                    popover: this.permissionDescription.popover,
                },
                {
                    icon: "fal fa-exclamation-triangle",
                    show: !this.survey.settings.notAnonymous && this.anonymityThreshold > 1,
                    iconBeforeLabel: true,
                    label: this.anonymityThreshold,
                    popover: this.$t("surveys.details.helpAnonymityThresholdDynamic"),
                },
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: !["action_items", "participation_rate"].includes(this.$route.params.tab),
                    label: this.$t("surveys.details.btnExport"),
                    handler: this.export,
                    isButton: true,
                },
                {
                    show: this.$route.params.tab === "heat_map",
                    label: this.$t("surveys.details.btnExportPNG"),
                    handler: this.exportPNG,
                    tabClass: "tw-ml-4",
                    isButton: true,
                },
                {
                    show: this.$route.params.tab === "action_items",
                    label: this.$t("surveys.details.labelCreateActionItem"),
                    tabClass: "btn-primary",
                    handler: this.createActionItem,
                    isButton: true,
                },
            ];
        },
        permissionDescription() {
            // visibility rights between tabs can differ (by default, you'll only have access to your own results), so we need dedicated labels and popovers for each tab

            const survey = this.survey;
            const currentTab = this.$route.params.tab.toUpperCase();

            // reset first

            let label = "";
            let popover = "";

            if (
                !survey.sharedWithMe.scope.includes(currentTab) ||
                survey.sharedWithMe.level === ""
            ) {
                // own results only

                label = this.$t("surveys.details.labelVisOwnResultsShort");
                popover = this.$t("surveys.details.labelVisOwnResults");
                return { label, popover };
            }

            if (survey.sharedWithMe.level == "ALL") {
                label = this.$t("surveys.details.labelVisAllResultsShort");
                popover += this.$t("surveys.details.labelVisAllResults");
            } else if (survey.sharedWithMe.level == "DIRECT_REPORTS") {
                label += this.$t("surveys.details.labelVisLimitedResultsShort");
                popover += this.$t("surveys.details.labelVisDirectReports");
            } else if (survey.sharedWithMe.level == "TEAM_MANAGER") {
                label += this.$t("surveys.details.labelVisLimitedResultsShort");
                popover += this.$t("surveys.details.labelVisTeamManager");
            } else if (survey.sharedWithMe.level == "TEAM_MEMBER") {
                label += this.$t("surveys.details.labelVisLimitedResultsShort");
                popover += this.$t("surveys.details.labelVisTeamMember");
            } else if (survey.sharedWithMe.level || survey.sharedWithMe.level == "") {
                label += this.$t("surveys.details.labelVisOwnResultsShort");
                popover += this.$t("surveys.details.labelVisOwnResults");
            }

            if (this.$route.params.tab == "list" && this.survey.sharedWithMe.level != "") {
                popover += this.$t("surveys.details.labelVisList");
            } else if (this.$route.params.tab == "heat_map") {
                popover += this.$t("surveys.details.labelVisHeatmap");
            }

            return { label, popover };
        },
    },
    watch: {
        "$route.params.id": {
            deep: true,
            immediate: true,
            handler(id) {
                this.survey = this.getDetail(id);
            },
        },
    },

    created() {
        this.icon = icon;
        const tab = this.$route.params.tab;
        const scope = (this.survey.sharedWithMe?.scope || []).filter(s =>
            ["LIST", "COMMENTS", "HEAT_MAP", "PARTICIPATION_RATE", "MULTI"].includes(s),
        );

        // redirect user when user loads a section that user does NOT have permission to see (but always allow comments and list for own results)

        if (
            !["COMMENTS", "ACTION_ITEMS"].includes(tab?.toUpperCase()) &&
            scope &&
            scope[0] &&
            !scope.includes(tab.toUpperCase())
        ) {
            this.$router.push(
                `/surveys/insights/${this.$route.params.id}/${scope[0].toLowerCase()}`,
            );
        }

        this.anonymityThreshold = this.survey?.settings?.anonymityThreshold;
    },
    methods: {
        changeAnonymity(anonymity) {
            this.anonymityThreshold = anonymity;
        },
        showTab(type) {
            if (type === "MULTI") {
                return this.permissionCheck(type) && this.survey.hasMultipleChoiceQuestions;
            } else {
                return this.permissionCheck(type);
            }
        },
        export() {
            this.$refs.currentTab.export();
        },
        exportPNG() {
            this.$refs.currentTab.exportPNG();
        },
        createActionItem() {
            this.$refs.currentTab.openActionItemModal({});
        },
        permissionCheck(tab) {
            if (this.rights.S_ACCESS_ALL) {
                return true;
            } else if (["COMMENTS"].includes(tab)) {
                return true;
            } else {
                return this.survey.sharedWithMe?.scope?.includes(tab);
            }
        },
    },
};
</script>

<style scoped></style>
